import React, { MouseEvent, useContext } from 'react';
import { ButtonIconPosition, Flex, FlexBehavior, FlexJustify } from 'react-magma-dom';
import { ExpandLessIcon, ExpandMoreIcon, FullscreenIcon, ModeIcon } from 'react-magma-icons';
import { RouteComponentProps } from '@reach/router';
import { pageNavigationRender } from '../Button/NavigationButton';
import { Button } from '../Button';
import { BottomNavigationIds, MAIN_CONTENT_SIZE, MoreCollectionEarly } from '../../constants';
import {
  BottomNavigationWrapper,
  StyledBottomNavigation,
  StyledBottomNavigationButtons,
  StyledBottomNavigationMore,
  StyledContentWrapper,
} from '../Styled';
import { Navigation } from './Navigation';
import { Accordion } from '../Accordion';
import { TeacherFocus } from './TeacherFocus';
import { TeacherFocus as TeacherFocusInterface } from '../../types';
import { More } from './More';
import { ScreenContext } from '../FullScreen';
import { TEXT_STRUCTURE, WRITING_PROFORMA } from '../../constants/navigation';
import { EditToolContext } from '../../contexts/editToolContext';
import { IAsset } from '../../constants/assets';

interface IButtonNavigation extends RouteComponentProps {
  assets: IAsset[];
  page?: string;
  textType?: string;
  focus?: Array<TeacherFocusInterface>;
  isActive: boolean;
  setActive: React.Dispatch<React.SetStateAction<boolean>>;
  expanded: number;
  setExpand: React.Dispatch<React.SetStateAction<number>>;
  prevBtnId: number;
  setActiveBtn: React.Dispatch<React.SetStateAction<number>>;
  onEditClick: () => void;
  isProforma: boolean;
  content: JSX.Element;
  setContent: (content: JSX.Element) => void;
  query: string;
  staticPage?: string;
}

export const DEFAULT_BTN = 0;

export const BottomNavigation = (props: IButtonNavigation): JSX.Element => {
  const {
    assets,
    isActive,
    setActive,
    expanded,
    setExpand,
    prevBtnId,
    setActiveBtn,
    isProforma,
    setContent,
    content,
    onEditClick,
    navigate,
    focus,
    query,
    page = TEXT_STRUCTURE,
    textType,
    staticPage,
  } = props;

  const [isFullScreen] = useContext(ScreenContext).fullScreen;
  const { isEditMode, handleLeavePage } = useContext(EditToolContext);

  const isWritingProforma = page === WRITING_PROFORMA;
  const showNavigation = !(isFullScreen && isWritingProforma);

  const handleNavigate = (url: string) => {
    navigate && navigate(url);
  };

  const redirectByClick = (page: string): void => {
    if (handleLeavePage(() => handleNavigate(page), false)) {
      return;
    }

    setActive(false);
    setActiveBtn(DEFAULT_BTN);
    setExpand(DEFAULT_BTN);
    handleNavigate(page);
  };

  const handleFullScreenClick = () => {
    const toUrl = location.pathname.replace('/default/', '/full-screen/') + location.search;

    if (handleLeavePage(() => handleNavigate(toUrl), true)) {
      return;
    }

    handleNavigate(toUrl);
  };

  const handleState = (event: MouseEvent) => {
    const targetId = Number(event.currentTarget.getAttribute('id'));
    setExpand(targetId);

    switch (true) {
      case prevBtnId != targetId:
        setActive(true);
        setActiveBtn(targetId);
        break;
      case prevBtnId == targetId:
        setActive(false);
        setActiveBtn(DEFAULT_BTN);
        setExpand(DEFAULT_BTN);
        break;
      default:
        setActive(true);
        setActiveBtn(DEFAULT_BTN);
    }

    switch (targetId) {
      case BottomNavigationIds.more:
        setContent(<More assets={isProforma ? [...assets, MoreCollectionEarly(query)] : assets} />);
        break;
      default:
        setContent(<TeacherFocus focuses={focus} page={page} />);
    }
  };

  const getIcon = (id: number) => {
    return expanded == id ? <ExpandLessIcon testId="expand-less-icon" /> : <ExpandMoreIcon testId="expand-more-icon" />;
  };

  const getAriaLabel = (id: number) => {
    return expanded == id ? 'expanded' : 'collapsed';
  };

  const renderNavigationChildren = () => {
    return (
      <BottomNavigationWrapper data-testid="navigation">
        <StyledBottomNavigation isMoreMenuOpen={isActive}>
          <StyledContentWrapper width={MAIN_CONTENT_SIZE.S[0]}>
            <StyledBottomNavigationButtons>
              <Flex behavior={FlexBehavior.container} justify={FlexJustify.spaceBetween}>
                <Flex behavior={FlexBehavior.item} justify={FlexJustify.flexStart}>
                  {!isFullScreen && (
                    <Button
                      text={'Full Screen'}
                      icon={<FullscreenIcon />}
                      iconPosition={ButtonIconPosition.right}
                      onClick={handleFullScreenClick}
                      ariaLabel={'FULLSCREEN'}
                    />
                  )}
                  {!isWritingProforma && (
                    <>
                      {assets?.length > 0 && (
                        <Button
                          id={`${BottomNavigationIds.more}`}
                          text={'More'}
                          icon={getIcon(BottomNavigationIds.more)}
                          iconPosition={ButtonIconPosition.right}
                          isActive={prevBtnId === BottomNavigationIds.more}
                          onClick={handleState}
                          ariaLabel={`MORE menu ${getAriaLabel(BottomNavigationIds.more)} subMenu`}
                        />
                      )}
                      {focus && (
                        <Button
                          id={`${BottomNavigationIds.teacherFocus}`}
                          text={'Teacher Focus'}
                          icon={getIcon(BottomNavigationIds.teacherFocus)}
                          iconPosition={ButtonIconPosition.right}
                          isActive={prevBtnId === BottomNavigationIds.teacherFocus}
                          onClick={handleState}
                          ariaLabel={`TEACHER FOCUS menu ${getAriaLabel(BottomNavigationIds.teacherFocus)} subMenu`}
                        />
                      )}
                      {!isEditMode && (
                        <Button
                          text={'Edit'}
                          onClick={onEditClick}
                          icon={<ModeIcon />}
                          iconPosition={ButtonIconPosition.right}
                        />
                      )}
                    </>
                  )}
                </Flex>
                {!staticPage && (
                  <Flex behavior={FlexBehavior.item} justify={FlexJustify.flexEnd}>
                    {pageNavigationRender(redirectByClick, page, textType)}
                  </Flex>
                )}
              </Flex>
            </StyledBottomNavigationButtons>
          </StyledContentWrapper>
        </StyledBottomNavigation>
        <StyledBottomNavigationMore>
          <StyledContentWrapper width={MAIN_CONTENT_SIZE.S[0]}>
            <Accordion isActive={isActive} content={content} />
          </StyledContentWrapper>
        </StyledBottomNavigationMore>
      </BottomNavigationWrapper>
    );
  };

  return <>{showNavigation && <Navigation isActive={isActive}>{renderNavigationChildren()}</Navigation>}</>;
};
